import React, { useCallback, useEffect, useState } from 'react';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import {
  billingAdminService,
  type BillingEntitlement,
  type BillingPlan,
} from '@/api/services/billingAdmin';
import { formatApiError } from '@/utils/formatApiError';
import { invalidatePlanDependentCaches } from '@/utils/planRefresh';
import { useWorkspace } from '@/context/WorkspaceContext';
import { DataTableScrollArea } from '@/components/table';

export default function BillingSubscriptionsPage() {
  const { toast } = useToast();
  const { invalidateWorkspaces } = useWorkspace();
  const [entitlements, setEntitlements] = useState<BillingEntitlement[]>([]);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [orgId, setOrgId] = useState('');
  const [planSlug, setPlanSlug] = useState('starter');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ent, planRes] = await Promise.all([
        billingAdminService.listEntitlements(),
        billingAdminService.listPlans(),
      ]);
      setEntitlements(ent.items);
      setPlans(planRes.items);
      if (planRes.items.length && !planRes.items.some((p) => p.slug === planSlug)) {
        setPlanSlug(planRes.items[0].slug);
      }
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [planSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const grant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId.trim()) return;
    try {
      await billingAdminService.grantEntitlement({
        organizationId: orgId.trim(),
        planSlug,
        expiresAt: expiresAt || undefined,
      });
      setOrgId('');
      setExpiresAt('');
      await load();
      await invalidatePlanDependentCaches({ invalidateWorkspaces });
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const executeRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    try {
      await billingAdminService.revokeEntitlement(revokeTarget);
      toast('Entitlement revoked', 'success');
      await load();
      await invalidatePlanDependentCaches({ invalidateWorkspaces });
    } catch (err) {
      const message = formatApiError(err);
      setError(message);
      toast(message, 'error');
    } finally {
      setRevokeBusy(false);
      setRevokeTarget(null);
    }
  };

  if (loading) return <div className="panel">Loading subscriptions…</div>;

  return (
    <div className="panel">
      <h1>Organization subscriptions</h1>
      <p className="muted">View and manually grant or revoke org entitlements.</p>
      {error ? <div className="alert alert-error">{error}</div> : null}

      <form className="form-grid" onSubmit={grant}>
        <h2>Grant plan</h2>
        <label>
          Organization ID
          <input required value={orgId} onChange={(e) => setOrgId(e.target.value)} />
        </label>
        <label>
          Plan
          <select value={planSlug} onChange={(e) => setPlanSlug(e.target.value)}>
            {plans.map((p) => (
              <option key={p.id} value={p.slug}>
                {p.name} ({p.slug})
              </option>
            ))}
          </select>
        </label>
        <label>
          Expires at (optional, ISO)
          <input value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} placeholder="2026-12-31T00:00:00Z" />
        </label>
        <button type="submit" className="btn btn-primary">
          Grant plan
        </button>
      </form>

      <h2>Entitlements</h2>
      <DataTableScrollArea>
        <table className="data-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Plan</th>
            <th>Status</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {entitlements.map((row) => (
            <tr key={row.id}>
              <td>
                {row.organizationName ?? row.organizationId}
                {row.organizationSlug ? ` (${row.organizationSlug})` : ''}
              </td>
              <td>{row.planName ?? row.planSlug ?? '—'}</td>
              <td>{row.status}</td>
              <td>{row.expiresAt ? new Date(row.expiresAt).toLocaleString() : '—'}</td>
              <td>
                {row.status === 'active' || row.status === 'manual_grant' ? (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRevokeTarget(row.id)}>
                    Revoke
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
        </DataTableScrollArea>
      <ConfirmActionModal
        open={Boolean(revokeTarget)}
        title="Revoke entitlement?"
        description="Active searches for this organization may be paused."
        confirmLabel="Revoke"
        variant="danger"
        loading={revokeBusy}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={executeRevoke}
      />
    </div>
  );
}
